import OutboundProductReference from './outbound-product-reference'
import { CardLoader } from 'components/organisms'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import { Alert, Button, Card, CardBody, Collapse, Label, ListGroup, ListGroupItem, Table } from 'reactstrap'
import StringUtil from 'utils/string-util'
import { useEffect, useState } from 'react'
import { UpdatePackingForm } from '../forms/update-packing-form'
import { UpdateShipmentForm } from '../forms/update-shipment-form'
import { DEAL_STATE } from 'domains/sales/deals/constants'
import TimeUtil from 'utils/time-util'
import TableUtil from 'utils/table-util'
import { printInvoice } from 'utils/invoice-util'
import OutboundDealsApi from 'api/admins/outbound-deals-api'

const propTypes = {
  deal: PropTypes.object,
  fetchDeal: PropTypes.func,
  updatePacking: PropTypes.func,
  updateShipment: PropTypes.func,
  orderPickup: PropTypes.func,
}

const defaultValues = {
  deal: {},
  fetchDeal: () => {},
  updatePacking: ({ready_to_ship, cart_item_updates}) => {},
  updateShipment: () => {},
  orderPickup: () => {},
}

/**
 * Displaying detail of Deal
 * @param {Object} deal
 */
const OutboundDealsView = ({
  deal,
  fetchDeal,
  cancelDeal,
  fetchHistoryList,
  historyList,
  fetchHistoryDetail,
  historyDetail,
  clearHistoryDetail,
  updatePacking,
  updateShipment,
  orderPickup
}) => {
  const payment = deal.payment
  const [ isHistoryOpen, setIsHistoryOpen ] = useState(false)
  const shipmentUseThirdParty = ["biteship", "shipper"].includes(deal.shipment.method_type)
  const [ showDifferenceOnly, setShowDifferenceOnly ] = useState(false)
  const [ isPrintingInvoice, setIsPrintingInvoice ] = useState(false)
  const [ printInvoiceResult, setPrintInvoiceResult ] = useState(null)

  useEffect(() => {
    if(isHistoryOpen){
      fetchHistoryList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isHistoryOpen])

  const tableData = TableUtil.convertHistoryDetailIntoTableData(historyDetail)

  const handlePrintInvoice = async (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (isPrintingInvoice) return

    setPrintInvoiceResult(null)
    setIsPrintingInvoice(true)

    try {
      await OutboundDealsApi.logPrint({
        dealId: deal.id,
        documentType: 'invoice',
        displayHeaderAndFooter: true
      })

      if (isHistoryOpen) fetchHistoryList()

      const result = await printInvoice(null, deal)
      const warnings = Array.isArray(result?.warnings) ? result.warnings : []
      setPrintInvoiceResult({
        color: warnings.length ? 'warning' : 'success',
        message: warnings.length
          ? `Invoice PDF berhasil dibuat. ${warnings.length} image/asset gagal dimuat dan dilewati.`
          : 'Invoice PDF berhasil dibuat.'
      })
    } catch (error) {
      setPrintInvoiceResult({
        color: 'danger',
        message: error?.response?.data?.message || error?.message || 'Invoice PDF gagal dibuat.'
      })
    } finally {
      setIsPrintingInvoice(false)
    }
  }

  return (
    <>
      <Card>
        <div className='p-3'>
          <div>
            <h2 className='mb-3 text-center'>{ `Summary (#${payment.id}) - ${payment.reference_number} - ${deal.reference_number}` }</h2>
              <div className="d-none d-md-flex mb-3">
                <Button onClick={fetchDeal} color="info">
                  Refresh Data
                </Button>
              </div>
            <Table
              bordered
              responsive
              striped
            >
              <tbody>
                <tr>
                  <th scope="row" className='text-right'>
                    Final Payment
                  </th>
                  <td>
                    {StringUtil.currencyCase(deal.final_payment)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Status
                  </th>
                  <td>
                    {StringUtil.capitalizeAll(StringUtil.humanize(deal.state))}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Pembayaran
                  </th>
                  <td>
                    {StringUtil.capitalizeAll(StringUtil.humanize(payment.method_type))}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Pengiriman
                  </th>
                  <td>
                    {
                      !!(deal.shipment?.courier_detail?.logistic?.name ?? "")
                      &&
                      <span className='text-sm' style={{display: "block"}}>{(deal.shipment?.courier_detail?.logistic?.name ?? "")}</span>
                    }
                    {
                      !!(deal.shipment?.courier_detail?.rate?.description ?? "")
                      &&
                      <span className='text-sm' style={{display: "block"}}>{(deal.shipment?.courier_detail?.rate?.description ?? "")}</span>
                    }
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Alamat
                  </th>
                  <td>
                    {
                      !!(deal.shipment?.destination?.address ?? "")
                      &&
                      <span className='text-sm' style={{display: "block"}}>{(deal.shipment?.destination?.address ?? "")}</span>
                    }
                    {
                      !!(deal.shipment?.destination?.direction ?? "")
                      &&
                      <span className='text-sm' style={{display: "block"}}>{(deal.shipment?.destination?.direction ?? "")}</span>
                    }
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    User Email
                  </th>
                  <td>
                    {deal.user.email}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    User Full Name
                  </th>
                  <td>
                    { deal.user?.id ? (
                      <a href={ `/app/sales/users/${deal.user.id}` } target='_blank' rel='noopener noreferrer'>
                        {deal.user?.detail?.name || deal.user?.name || deal.user?.email || "-"}
                      </a>
                    ) : (deal.user?.detail?.name || deal.user?.name || "-") }
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Product / SKU
                  </th>
                  <td>
                    { (deal.cart?.cart_items || []).length > 0 ? (
                      (deal.cart?.cart_items || []).map((cartItem) => {
                        const product = cartItem?.product
                        if (!product) return null

                        return (
                          <div key={ cartItem.id || product.id } className='mb-1'>
                            <OutboundProductReference product={ product } />
                          </div>
                        )
                      })
                    ) : '-' }
                  </td>
                </tr>
                <tr>
                  <th scope="row" className='text-right'>
                    Down Payment
                  </th>
                  <td>
                    {deal.down_payment ? StringUtil.currencyCase(deal.down_payment) : "-"}
                  </td>
                </tr>
              </tbody>
            </Table>
            {
              ![DEAL_STATE.COMPLETED, DEAL_STATE.ON_DELIVERY, DEAL_STATE.CANCELLED, DEAL_STATE.FAILED].includes(deal.state) && shipmentUseThirdParty
              &&
              <>
                <p className='font-weight-bold'>Pastikan saldo Biteship cukup sebelum Request Pickup</p>
                <Button onClick={orderPickup} color="warning">Request Pickup</Button>
              </>
            }
          </div>
        </div>
      </Card>
      <Card>
        <CardBody>
          <h2 className='mb-3 text-center'>Packing Data</h2>
          <UpdatePackingForm deal={deal} onSubmitForm={updatePacking} />
        </CardBody>
      </Card>
      {
        deal.ready_to_ship
        &&
        <Card>
          <CardBody>
            <h2 className='mb-3 text-center'>Shipment Data</h2>
            <UpdateShipmentForm deal={deal} onSubmitForm={updateShipment} />
          </CardBody>
        </Card>
      }
      <Card>
        <CardBody>
          <div className="d-flex flex-wrap align-items-center mb-3">
            <Button
              color="success"
              className="mr-2 mb-2"
              onClick={handlePrintInvoice}
              disabled={deal?.invoice_printable !== true || isPrintingInvoice}
              title={deal?.invoice_printable === true ? undefined : 'Invoice aktif setelah payment lunas / capture / settlement.'}
            >
              { isPrintingInvoice ? 'Preparing Invoice...' : 'Print Invoice' }
            </Button>
            <Button
              color="info"
              className="mb-2"
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            >
              Lihat History
            </Button>
          </div>
          {
            printInvoiceResult ? (
              <Alert color={ printInvoiceResult.color } className='mb-3'>
                { printInvoiceResult.message }
              </Alert>
            ) : null
          }
          <Collapse isOpen={isHistoryOpen}>
            <Card>
              <CardBody>
                <h2 className='mb-3 text-center'>History</h2>
                <ListGroup>
                  {
                    historyList.map((history) => {
                      const isActive = history.id === historyDetail.id
                      return (
                        <>
                          <ListGroupItem
                            action
                            active={isActive}
                            tag="button"
                            onClick={() => {
                              setShowDifferenceOnly(false)
                              if(isActive){
                                clearHistoryDetail()
                              }else{
                                fetchHistoryDetail(history.id)
                              }
                            }}
                          >
                            {TimeUtil.format(history.created_at, 'YYYY-MM-DD HH:mm:ss')}: ({history.done_by}) {history.action_type.replaceAll("_", " ")}
                          </ListGroupItem>
                          {
                            isActive
                            &&
                            <>
                              <Label className='text-gray-light text-nowrap mt-2'>
                                Lihat field yang berubah saja
                              </Label>
                              <Label className="custom-toggle mb-4">
                                <span></span>
                                <input
                                  type="checkbox"
                                  value={showDifferenceOnly}
                                  onClick={() => setShowDifferenceOnly(!showDifferenceOnly)}
                                />
                                <span
                                  className="custom-toggle-slider rounded-circle text-success"
                                  data-label-off="No"
                                  data-label-on="Yes"
                                />
                              </Label>
                              <Table striped>
                                <thead>
                                  <tr>
                                    <th>
                                      Field
                                    </th>
                                    <th>
                                      Before
                                    </th>
                                    <th>
                                      After
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {
                                    tableData
                                    .filter(o => {
                                      if(showDifferenceOnly){
                                        return !o.sameValue
                                      }
                                      return true
                                    })
                                    .map(o => {
                                      return (
                                        <tr>
                                          <th scope="row">
                                            {o.fieldName}
                                          </th>
                                          <td>
                                            {o.before}
                                          </td>
                                          <td>
                                            {o.after}
                                          </td>
                                        </tr>
                                      )
                                    })
                                  }
                                </tbody>
                              </Table>
                            </>
                          }
                        </>
                      )
                    })
                  }
                </ListGroup>
              </CardBody>
            </Card>
          </Collapse>
        </CardBody>
      </Card>
    </>
  )
}

OutboundDealsView.propTypes    = propTypes
OutboundDealsView.defaultProps = defaultValues

export default withLoader(OutboundDealsView, CardLoader)